/**
 * GraphSelectionContext - Shared state for the collaborators graph view.
 *
 * Provides:
 * - Active/selected + hovered node for opacity highlighting
 * - Adjacency map for neighbor lookups
 * - Animated edge pairs (currently always empty; reserved for future)
 * - Expanded node IDs for detail view
 */
import { createContext, useContext, type Context } from 'react';

export type GraphSelectionState = {
	activeNodeId: string;
	hoveredNodeId: string | null;
	centerNodeId: string;
	hasActiveSelection: boolean;
	adjacency: Map<string, Set<string>>;
	expandedNodeIds: Set<string>;
	animatedEdgePairs: Set<string>;
};

export const GraphSelectionContext: Context<GraphSelectionState | null> =
	createContext<GraphSelectionState | null>(null);

export function useGraphSelection(): GraphSelectionState | null {
	return useContext(GraphSelectionContext);
}

// ── Selection opacity ───────────────────────────────────────────────────

const FADED_OPACITY = 0.12;
const DEFAULT_EDGE_OPACITY = 0.4;
const SELECTED_EDGE_OPACITY = 0.7;
const FADED_EDGE_OPACITY = 0.05;
const BACKGROUND_NODE_Z_INDEX = 0;
const ACTIVE_NODE_Z_INDEX = 30;
const ADJACENT_NODE_Z_INDEX = 20;
const HOVERED_NODE_Z_INDEX = 40;
const HOVER_REVEALED_EDGE_ENDPOINT_Z_INDEX = 80;

function isDefaultMode(selection: GraphSelectionState | null): boolean {
	return !selection || !selection.hasActiveSelection;
}

function isSelectedMode(selection: GraphSelectionState | null): boolean {
	return !!selection && selection.hasActiveSelection;
}

function isActiveNode(nodeId: string, selection: GraphSelectionState | null): boolean {
	return !!selection && nodeId === selection.activeNodeId;
}

function isNodeAdjacentToActive(nodeId: string, selection: GraphSelectionState | null): boolean {
	if (!selection) {
		return false;
	}
	return selection.adjacency.get(selection.activeNodeId)?.has(nodeId) ?? false;
}

function isNodeAdjacentToHovered(nodeId: string, selection: GraphSelectionState | null): boolean {
	if (!selection?.hoveredNodeId) {
		return false;
	}
	return selection.adjacency.get(selection.hoveredNodeId)?.has(nodeId) ?? false;
}

function isNodeEndpointOfHoverRevealedEdge(
	nodeId: string,
	selection: GraphSelectionState | null,
): boolean {
	if (!selection?.hoveredNodeId) {
		return false;
	}
	const { activeNodeId, hoveredNodeId } = selection;
	if (isDefaultMode(selection)) {
		return nodeId === hoveredNodeId || isNodeAdjacentToHovered(nodeId, selection);
	}
	return (
		(nodeId === activeNodeId || nodeId === hoveredNodeId) &&
		isEdgeBetweenActiveAndHovered(activeNodeId, hoveredNodeId, selection)
	);
}

export function isEdgeBetweenActiveAndHovered(
	sourceId: string,
	targetId: string,
	selection: GraphSelectionState | null,
): boolean {
	if (!selection?.hoveredNodeId) {
		return false;
	}
	const { activeNodeId, hoveredNodeId } = selection;
	if (hoveredNodeId === activeNodeId) {
		return false;
	}
	return (
		(sourceId === activeNodeId && targetId === hoveredNodeId) ||
		(sourceId === hoveredNodeId && targetId === activeNodeId)
	);
}

export function isNodeTypeLabelVisible(
	nodeId: string,
	selection: GraphSelectionState | null,
	isHovered: boolean,
	isReactFlowSelected: boolean = false,
): boolean {
	return (
		isHovered ||
		isReactFlowSelected ||
		(isSelectedMode(selection) && isActiveNode(nodeId, selection))
	);
}

/**
 * Default-mode 1-hop preview: when no node is selected and the user is
 * hovering a node, edges incident to that hovered node should reveal
 * their relationship-verb labels (so the user can read the 1-hop
 * neighbourhood at a glance). In selected mode, edge-label visibility
 * is governed by the existing selected/hovered logic and is *not*
 * affected by this helper.
 */
export function isDefaultModeHoverIncidentEdge(
	sourceId: string,
	targetId: string,
	selection: GraphSelectionState | null,
): boolean {
	if (!isDefaultMode(selection) || !selection?.hoveredNodeId) {
		return false;
	}
	const { hoveredNodeId } = selection;
	return sourceId === hoveredNodeId || targetId === hoveredNodeId;
}

export function isHoverRevealedEdge(
	sourceId: string,
	targetId: string,
	selection: GraphSelectionState | null,
): boolean {
	return (
		isEdgeBetweenActiveAndHovered(sourceId, targetId, selection) ||
		isDefaultModeHoverIncidentEdge(sourceId, targetId, selection)
	);
}

export function computeNodeZIndex(nodeId: string, selection: GraphSelectionState | null): number {
	if (!selection) {
		return BACKGROUND_NODE_Z_INDEX;
	}
	if (isNodeEndpointOfHoverRevealedEdge(nodeId, selection)) {
		return HOVER_REVEALED_EDGE_ENDPOINT_Z_INDEX;
	}
	const isSelected = isSelectedMode(selection);
	const isActive = isActiveNode(nodeId, selection);
	const isAdjacent = isNodeAdjacentToActive(nodeId, selection);
	if (selection.hoveredNodeId === nodeId && (!isSelected || isActive || isAdjacent)) {
		return HOVERED_NODE_Z_INDEX;
	}
	if (isSelected && isActive) {
		return ACTIVE_NODE_Z_INDEX;
	}
	if (isSelected && isAdjacent) {
		return ADJACENT_NODE_Z_INDEX;
	}
	return BACKGROUND_NODE_Z_INDEX;
}

export function computeNodeOpacity(nodeId: string, selection: GraphSelectionState | null): number {
	if (isDefaultMode(selection)) {
		return 1;
	}
	if (isActiveNode(nodeId, selection)) {
		return 1;
	}
	if (isNodeAdjacentToActive(nodeId, selection)) {
		return 1;
	}
	if (selection?.hoveredNodeId === nodeId) {
		return 1;
	}
	return FADED_OPACITY;
}

export function computeEdgeOpacity(
	sourceId: string,
	targetId: string,
	selection: GraphSelectionState | null,
): number {
	if (!selection) {
		return DEFAULT_EDGE_OPACITY;
	}
	const { activeNodeId } = selection;
	if (isDefaultMode(selection)) {
		return DEFAULT_EDGE_OPACITY;
	}
	if (sourceId === activeNodeId || targetId === activeNodeId) {
		return SELECTED_EDGE_OPACITY;
	}
	return FADED_EDGE_OPACITY;
}

// ── Shortest path (BFS) ────────────────────────────────────────────────
